import { SCROLLBAR_SCROLL_HEIGHT } from '../constants';
import type {
  FixedPositionProperties,
  RelativePositionAlignment,
} from '../types';
import { getTargetElementFromNestedXpath } from './xpath';

const getElementCenter = (elementPosition: DOMRect) => {
  return {
    x: Math.round(elementPosition?.x + elementPosition?.width / 2),
    y: Math.round(elementPosition?.y + elementPosition?.height / 2),
  };
};

const isElementMoving = (
  elementPosition: DOMRect,
  prevElementPosition: DOMRect
): boolean => {
  if (!elementPosition || !prevElementPosition) {
    return false;
  }
  const elementCenter = getElementCenter(elementPosition);
  const prevElementCenter = getElementCenter(prevElementPosition);
  return (
    elementCenter.x !== prevElementCenter.x ||
    elementCenter.y !== prevElementCenter.y
  );
};

export const runAfterMotion = (
  xpathToMovingElement,
  onMotionEnd: () => void,
  intervalInMS = 50
) => {
  if (!xpathToMovingElement) {
    onMotionEnd();
    return;
  }
  let prevElementPosition = getTargetElementFromNestedXpath(
    xpathToMovingElement,
    document
  ).targetElement?.getBoundingClientRect();
  const id = setInterval(() => {
    const newElementPosition = getTargetElementFromNestedXpath(
      xpathToMovingElement,
      document
    ).targetElement?.getBoundingClientRect();
    if (!isElementMoving(newElementPosition, prevElementPosition)) {
      clearInterval(id);
      onMotionEnd();
    }
    prevElementPosition = newElementPosition;
  }, intervalInMS);
};

export const getVerticallyScrollableParentElement = (
  element: HTMLElement | null
): HTMLElement | null => {
  if (!element) {
    return null;
  }

  const elementOverflowY = window.getComputedStyle(element).overflowY;
  const hasVerticalScroll =
    element.clientHeight > 0 &&
    element.scrollHeight - SCROLLBAR_SCROLL_HEIGHT > element.clientHeight;

  const elementScrollParentHeightDifference =
    element.scrollHeight - (element.parentElement?.clientHeight || 0);
  const elementScrollEqualsParentHeight = !!(
    element.parentElement &&
    elementScrollParentHeightDifference > -5 &&
    elementScrollParentHeightDifference < 5
  );

  if (
    hasVerticalScroll &&
    !elementScrollEqualsParentHeight &&
    !(elementOverflowY === 'visible' && element.parentElement)
  ) {
    return element;
  }
  return getVerticallyScrollableParentElement(element.parentElement);
};

export function setStyles(
  element: HTMLElement,
  styles: Partial<CSSStyleDeclaration>
) {
  for (const attribute in styles) {
    element.style[attribute] = styles[attribute] as string;
  }
}

export function getElementByAttribute(
  attr: string,
  documentRoot: Document = document
): HTMLElement {
  return documentRoot.body?.querySelector(`[${attr}]`) as HTMLElement;
}

const generatePositionProperties = (props: FixedPositionProperties) => {
  const defaultPlacementValues: FixedPositionProperties = {
    top: null,
    bottom: null,
    left: null,
    right: null,
    transform: null,
  };

  return { ...defaultPlacementValues, ...props };
};
export const setPageRelatedTooltipPosition = (
  tooltip: HTMLElement,
  alignment: RelativePositionAlignment,
  position: 'fixed' | 'absolute',
  margin?: number
) => {
  const fixedPlacementsStyles: Record<
    RelativePositionAlignment,
    FixedPositionProperties
  > = {
    topStart: generatePositionProperties({
      top: '0',
      left: '0',
    }),
    topCenter: generatePositionProperties({
      top: '0',
      transform: 'translateX(-50%)',
      left: '50%',
    }),
    topEnd: generatePositionProperties({
      top: '0',
      right: '0',
    }),
    middleStart: generatePositionProperties({
      top: '50%',
      transform: 'translateY(-50%)',
      left: '0',
    }),
    middleCenter: generatePositionProperties({
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    }),
    middleEnd: generatePositionProperties({
      top: '50%',
      transform: 'translateY(-50%)',
      right: '0',
    }),
    bottomStart: generatePositionProperties({
      bottom: '0',
      left: '0',
    }),
    bottomCenter: generatePositionProperties({
      bottom: '0',
      left: '50%',
      transform: 'translateX(-50%)',
    }),
    bottomEnd: generatePositionProperties({
      bottom: '0',
      right: '0',
    }),
  };

  tooltip.style.position = position;
  if (margin) {
    tooltip.style.margin = `${margin}px`;
  }
  const fixedPositionProperties = fixedPlacementsStyles[
    alignment
  ] as FixedPositionProperties;

  for (const [property, value] of Object.entries(fixedPositionProperties)) {
    tooltip.style[property] = value;
  }
};
